import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './WhatsApp.css';

const HUBFIT_API_URL = process.env.REACT_APP_API_URL || 'https://apidashboard.teamgreekgods.ma/api';

const WhatsApp = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState('idle');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualCounts, setManualCounts] = useState({});

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const [storesRes, empRes] = await Promise.all([
        axios.get(`${HUBFIT_API_URL}/stores/stats`, {
          headers: { Authorization: `Bearer ${process.env.REACT_APP_TOKEN}` }
        }),
        axios.get(`${HUBFIT_API_URL}/employees`, {
          headers: { Authorization: `Bearer ${process.env.REACT_APP_TOKEN}` }
        })
      ]);

      const employees = empRes.data.data || [];
      const employeeMap = {};
      employees.forEach((emp) => {
        const storeId = emp.store_ids?.[0]?.id || emp.store_id;
        if (storeId) {
          employeeMap[storeId] = `${emp.first_name} ${emp.last_name}`;
        }
      });

      if (storesRes.data.success) {
        const stores = storesRes.data.data?.stores || [];
        const formattedGroups = stores.map((store) => ({
          id: store.id,
          wa_number: `WA ${store.code}`,
          name: store.name || store.email || '',
          assistant_name: employeeMap[store.id] || '',
          is_active: store.is_active === 1 || store.is_active === true,
          client_count: store.client_count || 0
        }));
        setGroups(formattedGroups);
      }
    } catch (error) {
      console.error('Failed to load groups', error);
    }
    setLoading(false);
  };

  const refreshFromAPI = async () => {
    setLoading(true);
    setApiStatus('loading');
    
    try {
      const [storesRes, empRes] = await Promise.all([
        axios.get(`${HUBFIT_API_URL}/stores/stats`, {
          headers: { Authorization: `Bearer ${process.env.REACT_APP_TOKEN}` }
        }),
        axios.get(`${HUBFIT_API_URL}/employees`, {
          headers: { Authorization: `Bearer ${process.env.REACT_APP_TOKEN}` }
        })
      ]);

      const employees = empRes.data.data || [];
      const employeeMap = {};
      employees.forEach((emp) => {
        const storeId = emp.store_ids?.[0]?.id || emp.store_id;
        if (storeId) {
          employeeMap[storeId] = `${emp.first_name} ${emp.last_name}`;
        }
      });

      if (storesRes.data.success) {
        const stores = storesRes.data.data?.stores || [];
        const updatedGroups = stores.map((store) => ({
          id: store.id,
          wa_number: `WA ${store.code}`,
          name: store.name || store.email || '',
          assistant_name: employeeMap[store.id] || '',
          is_active: store.is_active === 1 || store.is_active === true,
          client_count: store.client_count || 0
        }));

        setGroups(updatedGroups);
        setLastUpdate(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
        setApiStatus('ok');
      } else {
        setApiStatus('error');
      }
    } catch (error) {
      console.error('API fetch error', error);
      setApiStatus('error');
    }
    
    setLoading(false);
  };

  const openManualForm = () => {
    const counts = {};
    groups.forEach((g) => {
      counts[g.id] = g.client_count || 0;
    });
    setManualCounts(counts);
    setShowManualForm(true);
  };

  const saveManual = () => {
    const updatedGroups = groups.map((g) => ({
      ...g,
      client_count: parseInt(manualCounts[g.id]) || 0
    }));
    setGroups(updatedGroups);
    setLastUpdate(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
    setApiStatus('ok');
    setShowManualForm(false);
  };

  return (
    <div className="whatsapp-page">
      <div className="section-header">
        <div className="section-dot"></div>
        <h2>Clients par WhatsApp</h2>
      </div>

      <div className="api-bar">
        <div className={`api-dot ${apiStatus}`}></div>
        <span className="api-message">
          {apiStatus === 'ok' ? `Compteurs mis a jour - ${lastUpdate}` :
           apiStatus === 'loading' ? 'Chargement...' :
           apiStatus === 'error' ? 'Erreur de connexion API' :
           'Compteurs mis a jour manuellement'}
        </span>
        <button className="btn-outline" onClick={openManualForm}>Modifier les compteurs</button>
        <button className="btn-primary-outline" onClick={refreshFromAPI} disabled={loading}>
          {loading ? 'Chargement...' : 'Actualiser depuis API'}
        </button>
      </div>

      {showManualForm && (
        <div className="manual-form">
          <div className="form-title">Mettre a jour les compteurs clients</div>
          <div className="form-grid">
            {groups.map((group) => (
              <div key={group.id} className="form-row">
                <label>{group.wa_number}</label>
                <input
                  type="number"
                  value={manualCounts[group.id] || 0}
                  onChange={(e) => setManualCounts({ ...manualCounts, [group.id]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <div className="form-actions">
            <button className="btn-primary" onClick={saveManual}>Enregistrer</button>
            <button className="btn-secondary" onClick={() => setShowManualForm(false)}>Annuler</button>
          </div>
        </div>
      )}

      <div className="wac-grid">
        {groups.map((group) => (
          <div key={group.id} className={`wac-card ${!group.is_active ? 'inactive' : ''}`}>
            <div className="wac-header">
              <span className="wac-name">{group.wa_number}</span>
              {group.assistant_name && <span className="wac-assistant">{group.assistant_name}</span>}
            </div>
            <div className="wac-count">{group.client_count || '-'}</div>
            <div className="wac-label">
              {group.is_active ? 'clients actifs' : 'Groupe inactif'}
            </div>
          </div>
        ))}
      </div>

      <div className="last-update">
        Derniere mise a jour : <span>{lastUpdate || '-'}</span>
      </div>
    </div>
  );
};

export default WhatsApp;
